import React, { useEffect, useState } from 'react';
import { X, Type, AlignLeft, Shapes, Database, Archive, Tag } from 'lucide-react';
import { ingestionService } from '../../../../components/api/ingestionService';
import { apiService } from '../../../../components/api/apiService';
import { dataVaultService } from '../../../../components/api/dataVaultService';

type VectorStoreOption = { id: string; description: string };
type DataVaultOption = { id: string; name: string };

type Props = {
    onClose: () => void;
    onSubmit: (data: any) => void;
    tenantId: string;
    userId: string;
};

const KnowledgeEntityModal: React.FC<Props> = ({ onClose, onSubmit, tenantId, userId }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [tagInput, setTagInput] = useState('');
    const [selectedVectorStoreId, setSelectedVectorStoreId] = useState('');
    const [vectorStores, setVectorStores] = useState<VectorStoreOption[]>([]);
    const [selectedVaultId, setSelectedVaultId] = useState('');
    const [dataVaults, setDataVaults] = useState<DataVaultOption[]>([]);

    useEffect(() => {
        const loadVectorStores = async () => {
            try {
                const response = await ingestionService.getVectorStores();
                const list = Array.isArray(response) ? response : [];
                setVectorStores(list.map((vs: any) => ({ id: vs.id, description: vs.description || vs.collectionName || vs.id })));
                if (list.length > 0 && !selectedVectorStoreId) {
                    const defaultStore = list.find((vs: any) => vs.isDefault) || list[0];
                    setSelectedVectorStoreId(defaultStore.id);
                }
            } catch (err) {
                console.error('Failed to load vector stores', err);
            }
        };
        loadVectorStores();
    }, []);

    useEffect(() => {
        const loadDataVaults = async () => {
            try {
                const list = await dataVaultService.getList();
                setDataVaults((list || []).map((v) => ({ id: v.id, name: v.name })));
            } catch (err) {
                console.error('Failed to load data vaults', err);
            }
        };
        loadDataVaults();
    }, []);

    const handleSubmit = async () => {
        if (!title.trim()) return alert("Title is required.");

        const payload: Record<string, unknown> = {
            tenantId,
            title: title.trim(),
            description: description.trim(),
            createdByUserId: userId,
            tags,
            vectorstoreid: selectedVectorStoreId || null,
            vaultid: selectedVaultId || null
        };

        try {
            await apiService.post('/api/knowledgebase/createkbentity', payload, 'backend');
            onSubmit(payload);
            onClose();
        } catch (err) {
            console.error('Error submitting entity:', err);
            alert('Failed to create knowledge entity.');
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-neutral-900 border border-neutral-700 text-white rounded-xl shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-800">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                        <Shapes className="w-4 h-4 text-indigo-400" />
                        Create Knowledge Workspace
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-4 space-y-4 text-sm">
                    <div>
                        <label className="flex items-center gap-1 text-gray-300 mb-1">
                            <Type className="w-4 h-4 text-indigo-500" />
                            Title
                        </label>
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full bg-neutral-800 border border-neutral-600 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            placeholder="e.g. Finance Knowledge Group"
                        />
                    </div>

                    <div>
                        <label className="flex items-center gap-1 text-gray-300 mb-1">
                            <AlignLeft className="w-4 h-4 text-indigo-500" />
                            Description
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            className="w-full bg-neutral-800 border border-neutral-600 rounded px-3 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            placeholder="Short description about this entity group"
                        />
                    </div>

                    <div>
                        <label className="flex items-center gap-1 text-gray-300 mb-1">
                            <Database className="w-4 h-4 text-indigo-500" />
                            Default Vector store
                        </label>
                        <select
                            value={selectedVectorStoreId}
                            onChange={(e) => setSelectedVectorStoreId(e.target.value)}
                            className="w-full bg-neutral-800 border border-neutral-600 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                            <option value="">Select vector store</option>
                            {vectorStores.map((vs) => (
                                <option key={vs.id} value={vs.id}>
                                    {vs.description}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="flex items-center gap-1 text-gray-300 mb-1">
                            <Archive className="w-4 h-4 text-indigo-500" />
                            Default Data Vault
                        </label>
                        <select
                            value={selectedVaultId}
                            onChange={(e) => setSelectedVaultId(e.target.value)}
                            className="w-full bg-neutral-800 border border-neutral-600 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                            <option value="">Select data vault</option>
                            {dataVaults.map((v) => (
                                <option key={v.id} value={v.id}>
                                    {v.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="flex items-center gap-1 text-gray-300 mb-1">
                            <Tag className="w-4 h-4 text-indigo-500" />
                            Tags
                        </label>
                        <div className="flex flex-wrap gap-2 min-h-[38px] bg-neutral-800 border border-neutral-600 rounded px-2 py-1.5 focus-within:ring-1 focus-within:ring-indigo-500">
                            {tags.map((tag) => (
                                <span
                                    key={tag}
                                    className="inline-flex items-center gap-1 bg-neutral-700 text-gray-200 rounded pl-2 pr-1 py-0.5 text-xs"
                                >
                                    {tag}
                                    <button
                                        type="button"
                                        onClick={() => setTags((prev) => prev.filter((t) => t !== tag))}
                                        className="text-gray-400 hover:text-white rounded p-0.5 transition"
                                        aria-label={`Remove ${tag}`}
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </span>
                            ))}
                            <input
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ',') {
                                        e.preventDefault();
                                        const value = tagInput.replace(/,/g, '').trim();
                                        if (value && !tags.includes(value)) {
                                            setTags((prev) => [...prev, value]);
                                            setTagInput('');
                                        }
                                    }
                                }}
                                onBlur={() => {
                                    const value = tagInput.trim();
                                    if (value && !tags.includes(value)) {
                                        setTags((prev) => [...prev, value]);
                                        setTagInput('');
                                    }
                                }}
                                className="flex-1 min-w-[120px] bg-transparent border-0 py-0.5 px-1 text-sm focus:outline-none placeholder-gray-500"
                                placeholder={tags.length === 0 ? 'Add tags (Enter or comma)' : 'Add more…'}
                            />
                        </div>
                    </div>
                </div>

                <div className="px-4 py-3 border-t border-neutral-800 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="text-sm px-4 py-1.5 rounded bg-neutral-700 hover:bg-neutral-600 transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="text-sm px-4 py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 text-white transition"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export default KnowledgeEntityModal;
